"use client";

import { useEffect, useState } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import {
  clearWebAuthMarkerCookie,
  setWebAuthMarkerCookie,
} from "./auth-cookie";
import { demoUser } from "./demo-data";
import type { AuthResult, AuthUser } from "./types";

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  demoMode: boolean;
  setSession: (result: AuthResult) => void;
  updateUser: (user: AuthUser) => void;
  startDemoSession: () => void;
  logout: () => void;
};

type SessionLike = Pick<AuthState, "accessToken" | "user" | "demoMode">;

export function isUsableWebSession(state: SessionLike) {
  return Boolean(state.user && (state.demoMode || state.accessToken));
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      demoMode: false,
      setSession: (result) => {
        if (result.user && result.accessToken) {
          setWebAuthMarkerCookie();
        } else {
          clearWebAuthMarkerCookie();
        }
        set({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          user: result.user,
          demoMode: false,
        });
      },
      updateUser: (user) => set({ user }),
      startDemoSession: () => {
        setWebAuthMarkerCookie();
        set({
          accessToken: null,
          refreshToken: null,
          user: demoUser,
          demoMode: true,
        });
      },
      logout: () => {
        clearWebAuthMarkerCookie();
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          demoMode: false,
        });
      },
    }),
    {
      name: "skylight-web-client-auth-v1",
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        demoMode: state.demoMode,
      }),
      onRehydrateStorage: () => (state) => {
        if (state && isUsableWebSession(state)) {
          setWebAuthMarkerCookie();
        } else {
          clearWebAuthMarkerCookie();
        }
      },
    },
  ),
);

export function useAuthHydrated() {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setHydrated(true), 0);
    return () => window.clearTimeout(timer);
  }, []);

  return hydrated;
}
